import React, { useCallback, useEffect, useState } from 'react';
import {
  Layout, Typography, Button, Card, List, Modal, Spin, Alert, App, Form, Input, Drawer, Space, Tag, Switch, Row, Col,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleFilled, HistoryOutlined, UndoOutlined,
} from '@ant-design/icons';
import {
  getClientes, createCliente, updateCliente, deleteCliente, reativarCliente, getClienteHistorico,
} from '../../services/clienteService';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import { getErrorMessage } from '../../utils/errorHandler';

const { Content } = Layout;
const { Title, Text } = Typography;

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

const formatDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};

function ClientesPage() {
  const { message } = App.useApp();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clienteEmEdicao, setClienteEmEdicao] = useState(null);
  const [historicoOpen, setHistoricoOpen] = useState(false);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [historicoCliente, setHistoricoCliente] = useState(null);
  const [form] = Form.useForm();
  const [modal, modalContextHolder] = Modal.useModal();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchClientes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getClientes({ status: showArchived ? 'ARQUIVADO' : 'ATIVO' });
      setClientes(Array.isArray(data) ? data : data?.results || []);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao buscar clientes.'));
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  const handleAbrirCriar = () => {
    setClienteEmEdicao(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleAbrirEditar = (cliente) => {
    setClienteEmEdicao(cliente);
    form.setFieldsValue({
      nome: cliente.nome,
      telefone: cliente.telefone || '',
      email: cliente.email || '',
      observacoes: cliente.observacoes || '',
    });
    setIsModalOpen(true);
  };

  const handleSalvar = async (values) => {
    setSaving(true);
    try {
      const payload = {
        nome: values.nome.trim(),
        telefone: values.telefone?.trim() || null,
        email: values.email?.trim() || null,
        observacoes: values.observacoes?.trim() || null,
      };
      if (clienteEmEdicao) {
        await updateCliente(clienteEmEdicao.id, payload);
        message.success('Cliente atualizado.');
      } else {
        await createCliente(payload);
        message.success('Cliente criado.');
      }
      setIsModalOpen(false);
      fetchClientes();
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao salvar cliente.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletar = (cliente) => {
    modal.confirm({
      title: 'Arquivar cliente?',
      icon: <ExclamationCircleFilled />,
      content: `O cliente "${cliente.nome}" será arquivado. Lançamentos vinculados permanecem.`,
      okText: 'Arquivar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteCliente(cliente.id);
          message.success('Cliente arquivado.');
          fetchClientes();
        } catch (err) {
          message.error(getErrorMessage(err, 'Erro ao arquivar cliente.'));
        }
      },
    });
  };

  const handleReativar = async (cliente) => {
    try {
      await reativarCliente(cliente.id);
      message.success('Cliente reativado.');
      fetchClientes();
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao reativar cliente.'));
    }
  };

  const handleHistorico = async (cliente) => {
    setHistoricoCliente(cliente);
    setHistoricoOpen(true);
    setHistoricoLoading(true);
    try {
      const data = await getClienteHistorico(cliente.id);
      setHistorico(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao carregar histórico.'));
      setHistorico([]);
    } finally {
      setHistoricoLoading(false);
    }
  };

  if (loading && !isModalOpen) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <Spin tip="Carregando..." size="large" />
      </div>
    );
  }

  return (
    <Content style={{ padding: isMobile ? '1rem' : '2rem', width: '100%' }}>
      {modalContextHolder}
      <Row gutter={[16, 16]} style={{ marginBottom: '1.5rem' }} align="middle">
        <Col xs={24} md={12}>
          <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>Clientes</Title>
          <Text type="secondary">Cadastro leve para cobranças e histórico.</Text>
        </Col>
        <Col xs={24} md={12}>
          <div style={{ display: 'flex', gap: 16, justifyContent: isMobile ? 'flex-start' : 'flex-end', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <Text>Exibir arquivados</Text>
              <Switch checked={showArchived} onChange={setShowArchived} />
            </div>
            {!showArchived && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAbrirCriar} block={isMobile} size={isMobile ? 'large' : 'middle'}>
                Novo cliente
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {error && <Alert message="Erro" description={error} type="error" showIcon style={{ marginBottom: '1rem' }} />}

      <Card bordered={!isMobile} styles={{ body: { padding: isMobile ? 0 : 24 } }}>
        <List
          dataSource={clientes}
          locale={{
            emptyText: (
              <EmptyStateGuide
                title={showArchived ? 'Nenhum cliente arquivado.' : 'Nenhum cliente cadastrado.'}
                description={showArchived ? 'Clientes arquivados aparecem aqui.' : 'Cadastre quem te paga para organizar cobranças e ver o histórico.'}
                actionLabel={showArchived ? undefined : 'Novo cliente'}
                onAction={showArchived ? undefined : handleAbrirCriar}
              />
            ),
          }}
          renderItem={(cliente) => {
            const arquivado = cliente.status === 'ARQUIVADO' || showArchived;
            return (
              <List.Item
                style={{ padding: isMobile ? '12px 16px' : undefined }}
                actions={[
                  <Button key="h" icon={<HistoryOutlined />} type="text" onClick={() => handleHistorico(cliente)} />,
                  ...(arquivado
                    ? [
                        <Button key="r" icon={<UndoOutlined />} type="text" onClick={() => handleReativar(cliente)}>
                          Reativar
                        </Button>,
                      ]
                    : [
                        <Button key="e" icon={<EditOutlined />} type="text" onClick={() => handleAbrirEditar(cliente)} />,
                        <Button key="d" icon={<DeleteOutlined />} type="text" danger onClick={() => handleDeletar(cliente)} />,
                      ]),
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      {cliente.nome}
                      {arquivado && <Tag>Arquivado</Tag>}
                    </Space>
                  }
                  description={
                    <Space wrap size={4}>
                      {cliente.telefone && <Text type="secondary">{cliente.telefone}</Text>}
                      {cliente.email && <Text type="secondary">{cliente.email}</Text>}
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Card>

      <Modal
        title={clienteEmEdicao ? 'Editar cliente' : 'Novo cliente'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText="Salvar"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSalvar}>
          <Form.Item name="nome" label="Nome" rules={[{ required: true, message: 'Informe o nome.' }]}>
            <Input placeholder="Nome do cliente" maxLength={120} />
          </Form.Item>
          <Form.Item name="telefone" label="Telefone">
            <Input placeholder="(00) 00000-0000" maxLength={30} />
          </Form.Item>
          <Form.Item name="email" label="E-mail" rules={[{ type: 'email', message: 'E-mail inválido.' }]}>
            <Input placeholder="email@exemplo.com" />
          </Form.Item>
          <Form.Item name="observacoes" label="Observações">
            <Input.TextArea rows={3} placeholder="Notas opcionais" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={historicoCliente ? `Histórico — ${historicoCliente.nome}` : 'Histórico'}
        open={historicoOpen}
        onClose={() => setHistoricoOpen(false)}
        width={isMobile ? '100%' : 420}
      >
        {historicoLoading ? (
          <Spin />
        ) : (
          <List
            dataSource={historico}
            locale={{ emptyText: 'Nenhuma cobrança vinculada a este cliente.' }}
            renderItem={(t) => (
              <List.Item>
                <List.Item.Meta
                  title={t.descricao}
                  description={`${formatDate(t.data_vencimento)} · ${formatCurrency(t.valor)}`}
                />
                <Tag color={t.status === 'PAGO' ? 'green' : 'orange'}>{t.status === 'PAGO' ? 'Recebida' : 'Pendente'}</Tag>
              </List.Item>
            )}
          />
        )}
      </Drawer>
    </Content>
  );
}

export default ClientesPage;
